<script setup lang="ts">
import type { Result } from '@/classes/result';
import CustomButton from '@/components/CustomButton.vue';
import Dialog from '@/components/dialogs/Dialog.vue';
import { useAuthStore } from '@/stores/auth';
import { useEmployeeStore } from '@/stores/employee';
import type { PasswordScore } from '@/types/passwordScore';
import { processErrorWithFeedback } from '@/utils/error';
import toast from '@/utils/toast';
import { confirmPassword, password, required } from '@/utils/validation';
import { debounce, zxcvbnAsync } from '@zxcvbn-ts/core';
import { ref, useTemplateRef, watch } from 'vue';

const isDialogOpen = defineModel<boolean>();

const employeeStore = useEmployeeStore();
const authStore = useAuthStore();

const currentPasswordInput = ref('');
const passwordInput = ref('');
const confirmPasswordInput = ref('');
const score = ref<PasswordScore>({ value: 0, color: 'red' });
const form = useTemplateRef('form');
const isLoading = ref(false);

const debounceUseZxcvbn = debounce(useZxcvbn, 200);

function closeDialog() {
    form.value?.reset();
    isDialogOpen.value = false;
}

async function useZxcvbn() {
    if (!passwordInput.value) {
        score.value = { color: 'red-accent-4', value: 0 };
        return;
    }

    const result = await zxcvbnAsync(passwordInput.value);
    switch (result.score) {
        case 4:
            score.value = { color: 'green', value: 100 };
            break;
        case 3:
            score.value = { color: 'yellow', value: 75 };
            break;
        case 2:
            score.value = { color: 'orange', value: 50 };
            break;
        case 1:
            score.value = { color: 'red-darken-1', value: 25 };
            break;
        default:
            score.value = { color: 'red-accent-4', value: 0 };
            break;
    }
}

async function changePassword() {
    const validationResult = await form.value?.validate();
    if (!validationResult?.valid) return;

    isLoading.value = true;

    try {
        const changePasswordResult: Result = await employeeStore.changePassword(
            authStore.loggedInEmployee?._id ?? '',
            currentPasswordInput.value,
            passwordInput.value,
            confirmPasswordInput.value
        );

        if (changePasswordResult.status !== 200) {
            toast.error('PasswordUpdateFailed');
            return;
        }

        form.value?.reset();

        toast.success('PasswordUpdated');
        closeDialog();
    } catch (error) {
        processErrorWithFeedback('Wachtwoord wijzigen niet gelukt', error);
    } finally {
        isLoading.value = false;
    }
}

watch(passwordInput, async () => {
    debounceUseZxcvbn();
});
</script>

<template>
    <Dialog v-model="isDialogOpen" title="Wachtwoord wijzigen" :isLoading="isLoading">
        <v-form ref="form" @submit.prevent>
            <v-text-field label="Huidig wachtwoord" v-model="currentPasswordInput" type="password" required :rules="[required]" />
            <div class="strength-container">
                <v-progress-linear height="25" :color="score.color" v-model="score.value">
                    <template v-slot:default>Wachtwoord sterkte</template>
                </v-progress-linear>
            </div>
            <v-text-field label="Nieuw wachtwoord" v-model="passwordInput" type="password" required :rules="[required, password]" />
            <v-text-field
                label="Wachtwoord bevestigen"
                v-model="confirmPasswordInput"
                type="password"
                required
                :rules="[required, confirmPassword(passwordInput)]"
            />
        </v-form>
        <template #actions>
            <v-spacer />
            <CustomButton @click="changePassword" :loading="isLoading" text="Bevestigen" />
            <CustomButton color="red" @click="closeDialog" text="Annuleren" />
        </template>
    </Dialog>
</template>
